<script setup>
import { ref } from 'vue'
import { useToast } from 'vue-toastification'
import Signal from '@/api/Signal'

const props = defineProps({
  onSuggested: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['modal-close'])
const toast = useToast()

const url = ref('')
const submitting = ref(false)
const error = ref('')

function isValidUrl(str) {
  try {
    const u = new URL(str)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

async function submit() {
  error.value = ''

  if (!url.value) {
    error.value = 'Please enter a feed URL'
    return
  }

  if (!isValidUrl(url.value)) {
    error.value = 'Please enter a valid URL (http:// or https://)'
    return
  }

  submitting.value = true

  try {
    const result = await Signal.suggestFeed(url.value)

    if (result.status === 'success') {
      toast.success('Feed suggested — an admin will review it')
      props.onSuggested(result.data)
      emit('modal-close')
    } else if (result.status === 'conflict') {
      error.value = 'This feed is already tracked'
    } else if (result.status === 'validation_error') {
      error.value = result.message || 'Invalid feed URL'
    } else {
      error.value = 'An error occurred'
    }
  } catch (e) {
    error.value = 'An error occurred'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="suggest-feed">
    <h3 class="modal-title">Suggest Feed</h3>
    <p class="modal-description">
      Suggest an RSS or Atom feed for the signals page. An admin will review and enable it.
    </p>

    <!-- Error -->
    <div v-if="error" class="error-msg">
      <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
      {{ error }}
    </div>

    <!-- URL input -->
    <div class="input-section">
      <label class="input-label">Feed URL</label>
      <div class="url-input-wrap">
        <input
          v-model="url"
          type="text"
          placeholder="https://example.com/feed.xml"
          :disabled="submitting"
          @keydown.enter="submit"
        />
      </div>
    </div>

    <!-- Actions -->
    <div class="modal-actions">
      <button
        class="btn-submit"
        :disabled="submitting || !url"
        @click="submit"
      >
        <font-awesome-icon v-if="submitting" :icon="['fas', 'spinner']" spin />
        <font-awesome-icon v-else :icon="['fas', 'rss']" />
        {{ submitting ? 'Submitting...' : 'Suggest Feed' }}
      </button>
      <button class="btn-cancel" @click="emit('modal-close')" :disabled="submitting">
        Cancel
      </button>
    </div>
  </div>
</template>

<style scoped>
.suggest-feed {
  width: 480px;
  max-width: 90vw;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #f3f4f6;
  margin: 0 0 0.5rem 0;
}

.modal-description {
  font-size: 0.8125rem;
  color: #9ca3af;
  margin: 0 0 1.25rem 0;
}

.error-msg {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-radius: 0.375rem;
  color: #fca5a5;
  font-size: 0.8125rem;
  margin-bottom: 1rem;
}

.input-section {
  margin-bottom: 0;
}

.input-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 0.375rem;
}

.url-input-wrap {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  overflow: hidden;
  transition: border-color 0.15s ease;
}

.url-input-wrap:focus-within {
  border-color: #7357D2;
}

.url-input-wrap input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 0.625rem 0.75rem;
  color: #e5e7eb;
  font-size: 0.875rem;
  font-family: inherit;
}

.url-input-wrap input::placeholder {
  color: #4b5563;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.btn-submit {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(115, 87, 210, 0.15);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: 0.375rem;
  color: #a78bfa;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-submit:hover:not(:disabled) {
  background: rgba(115, 87, 210, 0.3);
  border-color: rgba(115, 87, 210, 0.6);
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  color: #9ca3af;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #d1d5db;
}
</style>
